import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useMyLines } from '#/entities/telephony'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { cn } from '#/shared/lib/utils'
import { PhoneIcon } from '#/shared/ui/icons'
import {
  CallDisabledHint,
  ConversationView,
  useStartCall,
} from '#/widgets/phone-panel'
import type { CallDisabledReason } from '#/widgets/phone-panel'

import { customerNumbers } from '../lib/customer-numbers'
import type { NumberOption } from '../lib/customer-numbers'

const CALL_CHIP_CLASS =
  'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-avatar-green-soft text-avatar-green transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-40'

/** Green call shortcut: dials the number already selected as the active chat
 *  tab — no picker, since the tab is the chosen number. */
function CallButton({
  active,
  ready,
  disabledReason,
  onCall,
}: {
  active: string
  ready: boolean
  disabledReason: CallDisabledReason | null
  onCall: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <CallDisabledHint reason={disabledReason}>
      <button
        type="button"
        onClick={() => onCall(active)}
        disabled={!ready}
        aria-label={t('detailSidebar.chatCall')}
        className={CALL_CHIP_CLASS}
      >
        <PhoneIcon className="size-4" />
      </button>
    </CallDisabledHint>
  )
}

/** Fixed number submenu: pick which customer number the chat targets, plus a
 *  shortcut to call (one number) or choose a number to call (several). */
function NumbersBar({
  options,
  active,
  onSelect,
  onCall,
  callReady,
  callDisabledReason,
}: {
  options: NumberOption[]
  active: string
  onSelect: (value: string) => void
  onCall: (value: string) => void
  callReady: boolean
  callDisabledReason: CallDisabledReason | null
}) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-white px-2.5 py-1">
      <div className="flex min-w-0 items-center gap-2 overflow-x-auto">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option.value)}
            className={cn(
              'flex h-6 shrink-0 cursor-pointer items-center rounded-xl border px-2 text-xs transition-colors',
              option.value === active
                ? 'border-blue-500 bg-blue-500 text-paper'
                : 'border-gray-200 text-gray-500 hover:bg-gray-50',
            )}
          >
            {option.display}
          </button>
        ))}
      </div>
      <CallButton
        active={active}
        ready={callReady}
        disabledReason={callDisabledReason}
        onCall={onCall}
      />
    </div>
  )
}

/**
 * Chat tab for the shipment sidebar: a fixed number picker over the SMS/call
 * thread with the shipment's customer, reusing the phone-panel conversation.
 * Picking a number opens (or starts) that conversation right here.
 */
export function SidebarChatTab({
  shipment,
}: {
  shipment: ShipmentDetail | undefined
}) {
  const { t } = useTranslation()
  const { call, ready, disabledReason } = useStartCall()
  const options = customerNumbers(shipment?.customer)
  const userId = shipment?.user?.id
  const [selected, setSelected] = useState<string>()
  // A conversation always needs a number, so keep one selected — the picked one
  // when it's still valid, otherwise fall back to the first.
  const active =
    options.find((option) => option.value === selected)?.value ??
    options[0]?.value

  // The composer's "From" picker reads outbound lines via useActiveLine, which
  // gates its fetch on the *phone panel* being open. This tab lives in the
  // shipment sidebar, so warm the shared (day-cached) lines here while a
  // conversation is visible — otherwise the picker stays empty until the phone
  // panel (or the telephony connection panel) is opened.
  useMyLines({ enabled: Boolean(active) })

  if (!active) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center">
        <span className="text-xs text-gray-500">
          {t('detailSidebar.chatEmpty')}
        </span>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <NumbersBar
        options={options}
        active={active}
        onSelect={setSelected}
        onCall={call}
        callReady={ready}
        callDisabledReason={disabledReason}
      />
      <div className="min-h-0 flex-1">
        <ConversationView
          target={{ phone: active, userId: userId ?? undefined }}
          hideHeader
          className="bg-paper-avatar"
        />
      </div>
    </div>
  )
}
